import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { business as bizApi, publicApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { RivetWordmark } from "@/components/RivetLogo";
import { IDS } from "@/constants/testIds";
import { ArrowRight, Building2 } from "lucide-react";

export default function Onboarding() {
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [tax, setTax] = useState("10");
  const [terms, setTerms] = useState("Payment due within 14 days of acceptance. Prices valid for 30 days.");
  const [currencies, setCurrencies] = useState([]);
  const [busy, setBusy] = useState(false);
  const { user, refresh } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    publicApi.currencies().then(({ data }) => setCurrencies(data));
    // Only skip onboarding if the business is fully onboarded — otherwise stay here
    // so users completing onboarding for the first time (e.g. Google login) don't bounce.
    bizApi.get()
      .then(({ data }) => { if (data?.onboarded) nav("/dashboard", { replace: true }); })
      .catch(() => {});
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await bizApi.create({
        name,
        tagline: tagline || null,
        phone: phone || null,
        address: address || null,
        primary_currency: currency,
        default_tax_rate: (parseFloat(tax) || 0) / 100,
        default_terms: terms || null,
      });
      await refresh();
      toast.success("Business set up. Let's build your first quote.");
      nav("/quotes/new");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save business");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={IDS.onboarding.page} className="min-h-screen bg-[#F1F5F9] px-5 py-10">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8"><RivetWordmark size={38}/></div>
        <div className="rounded-lg border border-slate-200 bg-white p-7">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-[#2563EB]">
            <Building2 size={14}/> Onboarding
          </div>
          <h1 className="mt-2 text-3xl font-black tracking-tight text-[#001F3F]" style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
            Set up your business
          </h1>
          <p className="mt-2 text-sm text-[#64748B]">Hi {user?.name?.split(" ")[0]}, tell us about your business so we can brand and price your quotes correctly.</p>

          <form onSubmit={submit} className="mt-6 space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Business name *</Label>
              <Input
                data-testid={IDS.onboarding.nameInput}
                required value={name} onChange={(e) => setName(e.target.value)}
                className="mt-1.5 h-11 border-slate-200"
                placeholder="e.g. RivetQuote Builders"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Tagline</Label>
              <Input value={tagline} onChange={(e) => setTagline(e.target.value)} className="mt-1.5 h-11 border-slate-200"/>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Phone</Label>
                <Input data-testid={IDS.onboarding.phoneInput} value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5 h-11 border-slate-200"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Address</Label>
                <Input data-testid={IDS.onboarding.addressInput} value={address} onChange={(e) => setAddress(e.target.value)} className="mt-1.5 h-11 border-slate-200"/>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Primary currency *</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger data-testid={IDS.onboarding.currencySelect} className="mt-1.5 h-11 border-slate-200">
                    <SelectValue/>
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {currencies.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Default tax rate (%)</Label>
                <Input data-testid={IDS.onboarding.taxInput} type="number" step="0.1" value={tax} onChange={(e) => setTax(e.target.value)} className="mt-1.5 h-11 border-slate-200"/>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Default terms &amp; conditions</Label>
              <Textarea value={terms} onChange={(e) => setTerms(e.target.value)} className="mt-1.5 border-slate-200 min-h-[100px]"/>
            </div>

            <Button
              data-testid={IDS.onboarding.submitBtn}
              type="submit" disabled={busy || !name}
              className="w-full h-12 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold"
            >
              {busy ? "Saving…" : "Save & continue"} <ArrowRight size={16} className="ml-1"/>
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
